import React, { createContext, useReducer, useCallback, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { useNotification } from '../hooks/useNotification';

const ContactsContext = createContext();

const initialState = {
  contacts: [],
  selectedContact: null,
  showArchived: false,
  isLoading: false,
  error: null,
};

const ACTIONS = {
  SET_CONTACTS: 'SET_CONTACTS',
  ADD_CONTACT: 'ADD_CONTACT',
  UPDATE_CONTACT: 'UPDATE_CONTACT',
  DELETE_CONTACT: 'DELETE_CONTACT',
  SELECT_CONTACT: 'SELECT_CONTACT',
  SET_SHOW_ARCHIVED: 'SET_SHOW_ARCHIVED',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  CLEAR_CONTACTS: 'CLEAR_CONTACTS',
};

function contactsReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_CONTACTS:
      return { ...state, contacts: action.payload };
    
    case ACTIONS.ADD_CONTACT:
      return { ...state, contacts: [...state.contacts, action.payload] };
    
    case ACTIONS.UPDATE_CONTACT:
      return {
        ...state,
        contacts: state.contacts.map(contact =>
          contact.id === action.payload.id ? { ...contact, ...action.payload.updates } : contact
        ),
        selectedContact: state.selectedContact?.id === action.payload.id
          ? { ...state.selectedContact, ...action.payload.updates }
          : state.selectedContact,
      };
    
    case ACTIONS.DELETE_CONTACT:
      return {
        ...state,
        contacts: state.contacts.filter(contact => contact.id !== action.payload),
        selectedContact: state.selectedContact?.id === action.payload ? null : state.selectedContact,
      };
    
    case ACTIONS.SELECT_CONTACT:
      return { ...state, selectedContact: action.payload };
    
    case ACTIONS.SET_SHOW_ARCHIVED:
      return { ...state, showArchived: action.payload };
    
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    case ACTIONS.CLEAR_CONTACTS:
      return { ...state, contacts: [], selectedContact: null };
    
    default:
      return state;
  }
}

export function ContactsProvider({ children }) {
  const [state, dispatch] = useReducer(contactsReducer, initialState);
  const { isAuthenticated, user } = useAuth();
  const { showNotification } = useNotification();

  const loadContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      const data = await api.getContacts(state.showArchived);
      dispatch({ type: ACTIONS.SET_CONTACTS, payload: data.contacts });
    } catch (error) {
      console.error('Load contacts error:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [isAuthenticated, state.showArchived]);

  const addContact = useCallback(async (contactUuid, alias) => {
    try {
      // First validate user exists
      const userInfo = await api.getUserByUuid(contactUuid);
      
      if (!userInfo) {
        showNotification('Contact not found. Please check the UUID.', 'error');
        return false;
      }
      
      const contact = await api.addContact(contactUuid, alias);
      dispatch({ type: ACTIONS.ADD_CONTACT, payload: contact });
      showNotification('Contact added successfully', 'success');
      return true;
    } catch (error) {
      showNotification(error.message, 'error');
      return false;
    }
  }, [showNotification]);

  const updateContact = useCallback(async (contactId, updates) => {
    try {
      const updatedContact = await api.updateContact(contactId, updates);
      dispatch({
        type: ACTIONS.UPDATE_CONTACT,
        payload: { id: contactId, updates: updatedContact }
      });
      showNotification('Contact updated', 'success');
      return true;
    } catch (error) {
      showNotification(error.message, 'error');
      return false;
    }
  }, [showNotification]);

  const deleteContact = useCallback(async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return false;
    }
    
    try {
      await api.deleteContact(contactId);
      dispatch({ type: ACTIONS.DELETE_CONTACT, payload: contactId });
      showNotification('Contact deleted', 'info');
      return true;
    } catch (error) {
      showNotification(error.message, 'error');
      return false;
    }
  }, [showNotification]);

  const archiveContact = useCallback(async (contactId, archive) => {
    try {
      await api.archiveContact(contactId, archive);
      await loadContacts();
      showNotification(archive ? 'Contact archived' : 'Contact unarchived', 'success');
      return true;
    } catch (error) {
      showNotification(error.message, 'error');
      return false;
    }
  }, [loadContacts, showNotification]);

  const selectContact = useCallback(async (contact) => {
    dispatch({ type: ACTIONS.SELECT_CONTACT, payload: contact });
  }, []);

  const fetchPublicKey = useCallback(async (uuid) => {
    try {
      const userInfo = await api.getUserByUuid(uuid);
      return {
        publicKey: userInfo.publicKey,
        keyVersion: userInfo.keyVersion,
        uuid: userInfo.uuid
      };
    } catch (error) {
      console.error('Fetch public key error:', error);
      showNotification('Failed to fetch user: ' + error.message, 'error');
      return null;
    }
  }, [showNotification]);

  const updateContactLastMessage = useCallback((contactId, timestamp) => {
    dispatch({
      type: ACTIONS.UPDATE_CONTACT,
      payload: {
        id: contactId,
        updates: { lastMessageAt: timestamp }
      }
    });
  }, []);

  const clearContacts = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_CONTACTS });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadContacts();
    }
  }, [isAuthenticated, loadContacts]);

  const value = {
    ...state,
    loadContacts,
    addContact,
    updateContact,
    deleteContact,
    archiveContact,
    selectContact,
    fetchPublicKey,
    updateContactLastMessage,
    clearContacts,
    setShowArchived: (show) => dispatch({ type: ACTIONS.SET_SHOW_ARCHIVED, payload: show }),
    dispatch,
    ACTIONS,
  };

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
}

export function useContacts() {
  const context = React.useContext(ContactsContext);
  if (!context) {
    throw new Error('useContacts must be used within ContactsProvider');
  }
  return context;
}